import {
  buildPermissionDefinitions,
  buildPermissionSections as buildFramePermissionSections,
  type LevelOption,
  type PermissionRowConfig,
  type PermissionSectionConfig,
  type ScopeOption,
} from "@kontextwerk/control-web-frame/routes/OrgaView/subviews/User/permission-sections"

export type PermissionKey =
  | "core.projects"
  | "core.users"
  | "core.roles"
  | "core.org_settings"
  | "core.session_details"
  | "core.billing"
  | "core.audit_log"
  | "core.analytics"
  | "core.notifications"
  | "core.kontext_index"
  | "core.kontext_answer_library"
  | "core.kontext_file_upload"
  | "core.kontext_website_index"
  | "core.kontext_sharepoint"
  | "core.werk_sessions"
  | "core.werk_telephony"
  | "termin.scheduling"

export type { LevelOption, PermissionRowConfig, PermissionSectionConfig, ScopeOption }

const fullLevelOptions: LevelOption[] = [
  { value: "none", name: "Kein Zugriff" },
  { value: "view", name: "Ansehen" },
  { value: "manage", name: "Verwalten" },
]

const SCHEDULING_PERMISSION_SECTIONS: PermissionSectionConfig[] = [
  {
    id: "termin",
    title: "Kehrblatt Control",
    description: "Aufträge, Termine und Kalender.",
    helpDialogId: "userPermissionsKehrblattControl",
    helpDialogTooltip: "So funktionieren Kehrblatt-Control-Berechtigungen",
    rows: [
      {
        key: "termin.scheduling",
        label: "Aufträge & Termine",
        levelOptions: fullLevelOptions,
        description: "Steuert gemeinsam den Zugriff auf Aufträge, Termine und Kalender.",
        scopeTooltip:
          "Zum eigenen Arbeitsbereich gehören direkt zugewiesene Aufträge sowie vollständige Auftragsverbünde, sobald mindestens ein weiterhin verknüpfter Termin – auch ein historischer – dem Mitarbeiter zugewiesen ist. Freie Kalenderblöcke gehören nur bei ausdrücklicher Zuweisung dazu.",
        scopeOptions: [
          { value: "own", name: "Eigener Arbeitsbereich" },
          { value: "all", name: "Gesamte Organisation" },
        ],
      },
    ],
  },
]

const applyKehrblattCoreCopy = (sections: PermissionSectionConfig[]): PermissionSectionConfig[] =>
  sections.map((section) => {
    if (section.id !== "core") return section

    return {
      ...section,
      rows: section.rows.map((row) => {
        if (row.key === "core.notifications") {
          return {
            ...row,
            label: "Mitteilungen",
          }
        }

        if (row.key === "core.analytics") {
          return {
            ...row,
            label: "KI-Session-Auswertung",
          }
        }

        if (row.key === "core.org_settings") {
          return {
            ...row,
            description: "Zugriff auf Organisationsdaten und Kehrblatt-Einstellungen.",
            levelTooltip:
              "In Kehrblatt umfasst Organisation unter anderem allgemeine Einstellungen, Auftragstypen, Telefonnummern und je nach Tarif/Rolle Kontaktprofile.",
          }
        }

        if (row.key === "core.billing") {
          return {
            ...row,
            description: "Zugriff auf Abrechnung, Abo und abrechnungs- oder rechtliche Dokumente.",
            levelTooltip:
              "Dokumente haben keine eigene Permission. Sie hängen derzeit an Abrechnung & Abo und zusätzlich an Rolle sowie Billing-Verfügbarkeit.",
          }
        }

        return row
      }),
    }
  })

export const APP_PERMISSION_SECTIONS: PermissionSectionConfig[] = [
  ...SCHEDULING_PERMISSION_SECTIONS,
]
export const PERMISSION_SECTIONS: PermissionSectionConfig[] = applyKehrblattCoreCopy(
  buildFramePermissionSections(APP_PERMISSION_SECTIONS),
)
export const INVITE_PERMISSION_SECTIONS: PermissionSectionConfig[] = PERMISSION_SECTIONS.filter(
  (section) => ["core", "termin"].includes(section.id),
)
export const PERMISSION_DEFINITIONS = buildPermissionDefinitions(PERMISSION_SECTIONS)
export const PERMISSION_KEYS: PermissionKey[] = PERMISSION_SECTIONS.flatMap((section) =>
  section.rows.map((row) => row.key as PermissionKey),
) as PermissionKey[]
